import React, { useEffect, useState } from "react";
import { httpclient } from "../../../utils/httpClient";
import {
  CircularProgress,
  Snackbar,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl as MuiFormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import LoadingScreen from "../../../Components/LoadingScreen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MarketPlace = () => {
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [marketplaceList, setMarketplaceList] = useState([]);
  const [newMarketplaceList, setNewMarketplaceList] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    fetchMarketplaceData();
  }, []);

  const fetchMarketplaceData = () => {
    httpclient.get(`marketplace-setting`).then(({ data }) => {
      if (data.success) {
        setMarketplaceLoading(false);
        setMarketplaceList(data.data);
        setNewMarketplaceList(data.data);
      } else {
        setMarketplaceLoading(false);
      }
    });
  };

  useEffect(() => {
    if (marketplaceList.length > 0) setNewMarketplaceList(marketplaceList);
  }, [marketplaceList]);

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleMarketplaceSettingChange = (marketplaceIndex, settingIndex, newValue) => {
    const newMarketplaceLists = [...newMarketplaceList];
    newMarketplaceLists[marketplaceIndex].settings[settingIndex].value = newValue;
    setNewMarketplaceList(newMarketplaceLists);
  };

  const handleMultiselectChange = (marketplaceIndex, settingIndex, branchId, checked) => {
    const marketplace = newMarketplaceList[marketplaceIndex];
    const setting = marketplace.settings[settingIndex];
    let currentValues = Array.isArray(setting.value) ? setting.value : [];
    
    if (checked) {
      if (!currentValues.includes(branchId)) {
        currentValues = [...currentValues, branchId];
      }
    } else {
      currentValues = currentValues.filter(id => id !== branchId);
    }
    
    handleMarketplaceSettingChange(marketplaceIndex, settingIndex, currentValues);
  };

  const renderMarketplaceSettingField = (setting, marketplaceIndex, settingIndex) => {
    switch (setting.type) {
      case "radio":
        return (
          <MuiFormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">{setting.setting_name}</FormLabel>
            <RadioGroup
              row
              name={`radio-${marketplaceIndex}-${settingIndex}`}
              value={String(setting.value)}
              onChange={(e) => handleMarketplaceSettingChange(marketplaceIndex, settingIndex, e.target.value)}
            >
              <FormControlLabel value="1" control={<Radio />} label="Yes" />
              <FormControlLabel value="0" control={<Radio />} label="No" />
            </RadioGroup>
          </MuiFormControl>
        );
      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={setting.value === "1"}
                onChange={(e) =>
                  handleMarketplaceSettingChange(
                    marketplaceIndex,
                    settingIndex,
                    e.target.checked ? "1" : "0"
                  )
                }
              />
            }
            label={setting.setting_name}
            sx={{ mb: 1 }}
          />
        );
      case "multiselect":
        const branches = setting.branches || [];
        const selectedValues = Array.isArray(setting.value) ? setting.value : [];
        return (
          <MuiFormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">{setting.setting_name}</FormLabel>
            <FormGroup row sx={{ flexWrap: "wrap", gap: 1 }}>
              {branches.map((branch) => (
                <FormControlLabel
                  key={branch.erpBranchID}
                  control={
                    <Checkbox
                      checked={selectedValues.includes(branch.erpBranchID)}
                      onChange={(e) =>
                        handleMultiselectChange(
                          marketplaceIndex,
                          settingIndex,
                          branch.erpBranchID,
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={branch.branchName}
                />
              ))}
            </FormGroup>
          </MuiFormControl>
        );
      default:
        return null;
    }
  };

  const handleSubmit = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    httpclient.post(`marketplace-setting`, newMarketplaceList)
      .then(({ data }) => {
        setLoading(false);
        if (data.success) {
          setOpen(true);
          setMessage("Marketplace settings updated successfully");
          setMessageState("success");
          setExpandedAccordion(null);
          fetchMarketplaceData();
        } else {
          setOpen(true);
          setMessage(data.error || data.message);
          setMessageState("error");
        }
      })
      .catch(error => {
        setLoading(false);
        setOpen(true);
        setMessage("An error occurred while updating marketplace settings");
        setMessageState("error");
      });
  };

  const handleCancelSubmit = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <>
      {marketplaceLoading ? (
        <LoadingScreen message="fetching Data ..." />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Marketplace Settings
            </Typography>
          </Grid>
          
          {newMarketplaceList.map((marketplace, marketplaceIndex) => (
            <Grid item xs={12} key={marketplace.marketplaceId}>
              <Accordion
                expanded={expandedAccordion === `marketplace-panel${marketplaceIndex}`}
                onChange={handleChangeAccordion(`marketplace-panel${marketplaceIndex}`)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`marketplace-panel${marketplaceIndex}-content`}
                  id={`marketplace-panel${marketplaceIndex}-header`}
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    {marketplace.marketplaceName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {marketplace.settings.map((setting, settingIndex) => (
                    <div key={settingIndex}>
                      {renderMarketplaceSettingField(setting, marketplaceIndex, settingIndex)}
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}

          <Grid item xs={12} sx={{ textAlign: "right", pt: 2, m: 2 }}>
            {loading ? (
              <CircularProgress
                style={{ height: "20px", width: "20px", position: "relative" }}
              />
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      )}
      
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSubmit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Submission"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to save these marketplace settings? This action will update the configuration for all selected marketplaces.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSubmit} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        onClose={handleCloseSnack}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={messageState}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MarketPlace;